import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { SORTED_TERMS, LETTERS } from '../data/glossaryTerms';

const CATEGORY_COLORS = {
  'Basics':     '#38bdf8',
  'Investing':  '#a78bfa',
  'Retirement': '#22c55e',
  'Debt':       '#ef4444',
  'Savings':    '#f59e0b',
  'Markets':    '#22c55e',
  'Taxes':      '#f472b6',
  'Credit':     '#fb923c',
};

export default function Glossary() {
  const [search, setSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState('All');

  const filtered = useMemo(() => {
    let terms = SORTED_TERMS;
    if (activeLetter !== 'All') {
      terms = terms.filter(t => t.letter === activeLetter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      terms = terms.filter(t =>
        t.term.toLowerCase().includes(q) ||
        t.shortDef.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    }
    return terms;
  }, [search, activeLetter]);

  return (
    <div style={s.page}>
      <SEO
        title="Financial Glossary — Every Finance Term Explained in Plain English"
        description="120+ financial terms defined clearly — no jargon, no fluff. APR, compound interest, Roth IRA, index funds, and more. Free from KnowFi."
        path="/glossary"
        keywords="financial glossary, finance terms, APR definition, compound interest, Roth IRA explained, investment terms"
      />
      <div style={s.glow} />
      <div style={s.inner}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.badge}>📖 Free Resource</div>
          <h1 style={s.title}>Financial Glossary</h1>
          <p style={s.subtitle}>Every finance term you need, explained in plain English. No jargon. No fluff.</p>
        </div>

        {/* Search */}
        <div style={s.searchWrap}>
          <span style={s.searchIcon}>🔍</span>
          <input
            style={s.search}
            type="text"
            placeholder="Search terms, e.g. 'compound interest' or 'Roth IRA'..."
            value={search}
            onChange={e => { setSearch(e.target.value); setActiveLetter('All'); }}
          />
          {search && (
            <button style={s.clearBtn} onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        {/* Letter nav */}
        {!search && (
          <div style={s.letterNav}>
            <button
              style={{ ...s.letterBtn, ...(activeLetter === 'All' ? s.letterBtnActive : {}) }}
              onClick={() => { setActiveLetter('All'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >All</button>
            {LETTERS.map(l => (
              <button
                key={l}
                style={{ ...s.letterBtn, ...(activeLetter === l ? s.letterBtnActive : {}) }}
                onClick={() => { setActiveLetter(l); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >{l}</button>
            ))}
          </div>
        )}

        {/* Stats bar */}
        <div style={s.statsBar}>
          <span style={s.statText}>{filtered.length} {filtered.length === 1 ? 'term' : 'terms'}{activeLetter !== 'All' && !search ? ` starting with "${activeLetter}"` : ''}</span>
          {activeLetter !== 'All' && !search && (
            <button style={s.resetBtn} onClick={() => setActiveLetter('All')}>Show all →</button>
          )}
        </div>

        {/* Term grid */}
        {filtered.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>🔍</div>
            <div style={s.emptyText}>No terms match "{search}"</div>
            <button style={s.resetBtn} onClick={() => setSearch('')}>Clear search</button>
          </div>
        ) : (
          <div style={s.grid}>
            {filtered.map(term => {
              const color = CATEGORY_COLORS[term.category] || '#64748b';
              return (
                <Link key={term.slug} to={`/glossary/${term.slug}`} style={s.card}>
                  <div style={s.cardTop}>
                    <span style={{ ...s.catTag, background: color + '18', color, border: `1px solid ${color}33` }}>
                      {term.category}
                    </span>
                  </div>
                  <div style={s.termName}>{term.term}</div>
                  <div style={s.termShort}>{term.shortDef}</div>
                  <div style={s.readMore}>Read definition →</div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div style={s.bottomCta}>
          <div style={s.ctaText}>
            <strong style={{ color: '#f1f5f9' }}>Ready to put these terms to work?</strong>
            <span style={{ color: '#64748b' }}> KnowFi turns financial education into a personalized action plan.</span>
          </div>
          <Link to="/gameplan" style={s.ctaBtn}>Build My Free Plan →</Link>
        </div>

      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(167,139,250,0.07) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 },

  header:   { textAlign: 'center', marginBottom: '40px' },
  badge:    { display: 'inline-block', background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', fontWeight: '700', marginBottom: '16px', letterSpacing: '0.3px' },
  title:    { fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-1px', color: '#f1f5f9' },
  subtitle: { color: '#64748b', fontSize: '17px', margin: 0 },

  searchWrap: { position: 'relative', marginBottom: '24px' },
  searchIcon: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', pointerEvents: 'none' },
  search:   { width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', color: '#f8fafc', padding: '14px 44px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  clearBtn: { position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit', padding: '4px 8px', borderRadius: '6px' },

  letterNav:      { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '20px' },
  letterBtn:      { background: '#1e293b', border: '1px solid #334155', color: '#64748b', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', minWidth: '36px', textAlign: 'center' },
  letterBtnActive:{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa' },

  statsBar: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  statText: { color: '#475569', fontSize: '13px' },
  resetBtn: { background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', padding: 0 },

  empty:     { textAlign: 'center', padding: '60px 24px' },
  emptyIcon: { fontSize: '40px', marginBottom: '12px' },
  emptyText: { color: '#475569', fontSize: '16px', marginBottom: '16px' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px', marginBottom: '48px' },
  card: { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '8px', transition: 'border-color 0.2s' },
  cardTop:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  catTag:   { fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  termName: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', lineHeight: 1.3 },
  termShort:{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, flex: 1 },
  readMore: { fontSize: '12px', color: '#a78bfa', fontWeight: '600', marginTop: '4px' },

  bottomCta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', background: '#1e293b', borderRadius: '16px', padding: '24px 28px', border: '1px solid #334155', marginTop: '16px' },
  ctaText:   { fontSize: '15px', lineHeight: 1.5 },
  ctaBtn:    { background: '#a78bfa', color: '#0f172a', padding: '12px 22px', borderRadius: '10px', fontWeight: '700', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },
};
